import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Loader2 } from "lucide-react";
import apiClient from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { notificationTarget } from "@/components/notifications/NotificationBell";

function timeAgo(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback((p = page, f = filter) => {
    setLoading(true);
    const q = `?page=${p}&limit=20${f === "unread" ? "&unread_only=true" : ""}`;
    apiClient.get(`/notifications${q}`).then((r) => {
      setItems(r.data?.data || []);
      setPages(r.data?.pages || 0);
      setUnread(r.data?.unread || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [page, filter]);

  useEffect(() => { load(page, filter); }, [page, filter, load]);

  const onItem = async (n) => {
    if (!n.is_read) { try { await apiClient.post(`/notifications/${n.id}/read`); } catch { /* ignore */ } }
    navigate(notificationTarget(n, user?.role));
  };

  const markAll = async () => { try { await apiClient.post("/notifications/read-all"); load(page, filter); } catch { /* ignore */ } };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="notifications-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><Bell size={26} /> Notifikasi</h1>
        <button onClick={markAll} data-testid="notifications-mark-all"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60">
          <Check size={14} /> Tandai semua dibaca
        </button>
      </div>

      <div className="mt-5 flex gap-2">
        {[{ v: "all", l: "Semua" }, { v: "unread", l: `Belum dibaca${unread ? ` (${unread})` : ""}` }].map((t) => (
          <button key={t.v} onClick={() => { setPage(1); setFilter(t.v); }} data-testid={`notifications-filter-${t.v}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === t.v ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary/60"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" size={26} /></div>
      ) : items.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-secondary/30 p-12 text-center" data-testid="notifications-empty">
          <Bell size={26} className="mx-auto mb-2 text-primary" />
          <p className="text-sm text-muted-foreground">Tidak ada notifikasi.</p>
        </div>
      ) : (
        <div className="mt-6 rounded-2xl border border-border overflow-hidden" data-testid="notifications-list">
          {items.map((n, i) => (
            <button key={n.id} onClick={() => onItem(n)} data-testid={`notifications-item-${i}`}
              className={`w-full text-left px-5 py-4 border-b border-border/60 last:border-b-0 hover:bg-secondary/50 transition-colors ${n.is_read ? "bg-card" : "bg-secondary/30"}`}>
              <div className="flex items-start gap-2.5">
                {!n.is_read && <span className="mt-2 w-2 h-2 rounded-full bg-accent shrink-0" />}
                <div className={`min-w-0 ${n.is_read ? "pl-4" : ""}`}>
                  <p className="text-sm font-semibold">{n.title}</p>
                  <p className="text-sm text-muted-foreground">{n.message}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="mt-5 flex items-center justify-center gap-2" data-testid="notifications-pagination">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-4 py-2 rounded-full border border-border text-sm font-semibold disabled:opacity-50">Sebelumnya</button>
          <span className="text-sm text-muted-foreground">Hal {page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="px-4 py-2 rounded-full border border-border text-sm font-semibold disabled:opacity-50">Berikutnya</button>
        </div>
      )}
    </div>
  );
}
